'use client';

import { useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

export function useHorizontalScrollIndicators<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [scrollbarGutter, setScrollbarGutter] = useState({ bottom: 0, right: 0 });

  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateScrollIndicators = () => {
      const overflowX = getComputedStyle(element).overflowX;
      const overflowY = getComputedStyle(element).overflowY;
      const isHorizontallyScrollable = overflowX === 'auto' || overflowX === 'scroll';
      const isVerticallyScrollable = overflowY === 'auto' || overflowY === 'scroll';

      setIsScrollable(
        (isHorizontallyScrollable && element.scrollWidth > element.clientWidth + 1) ||
          (isVerticallyScrollable && element.scrollHeight > element.clientHeight + 1),
      );

      setCanScrollLeft(isHorizontallyScrollable && element.scrollLeft > 1);
      setCanScrollRight(
        isHorizontallyScrollable && element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
      );
      const bottom = element.offsetHeight - element.clientHeight;
      const right = element.offsetWidth - element.clientWidth;
      setScrollbarGutter((prev) => (prev.bottom === bottom && prev.right === right ? prev : { bottom, right }));
    };

    updateScrollIndicators();
    element.addEventListener('scroll', updateScrollIndicators, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollIndicators);
    resizeObserver.observe(element);
    if (element.firstElementChild) resizeObserver.observe(element.firstElementChild);

    return () => {
      element.removeEventListener('scroll', updateScrollIndicators);
      resizeObserver.disconnect();
    };
  }, []);

  return { ref, canScrollLeft, canScrollRight, isScrollable, scrollbarGutter };
}
